import moment from "moment";
import { uniq } from "lodash";
import buildSummaryData from "./buildSummaryData";
import {
  CARD_PADDING_PX,
  TYPE,
  count,
  createDocument,
  drawDivider,
  drawTableCard,
  drawText,
  ensureSpace,
  renderEntityCard,
  renderFooter,
  renderHeader,
  renderNotice,
  renderStatCards,
  tableHeight,
  textHeight,
} from "~/components/utils/pdfLayout";

// The row of file statistics the page renders as `FileStats`, minus its per-file View button.
function renderFilesCard(layout, submissions) {
  const { u } = layout;
  const padding = u(12);
  const nameHeight = textHeight(layout, TYPE.body, 1);

  // Each file names itself over its original row count, so its cell takes two lines.
  const fileCellHeight = padding * 2 + nameHeight + u(4) + textHeight(layout, TYPE.description, 1);

  const rows = submissions.map((submission) => {
    const summary = submission.summary ?? {};

    return [
      submission.filename || "File",
      count(summary.rows),
      count(submission.excludedRows),
      count(summary.valid),
      count(summary.invalid),
    ];
  });

  drawTableCard(layout, {
    head: [["File", "Selected Rows", "Invalid NDCs", "Rows for Submission", "Validation Alerts"]],
    body: rows,
    columnStyles: { 0: { fontStyle: "medium", minCellHeight: fileCellHeight } },
    hooks: {
      // The row count under each filename, which is a subtitle on the page rather than a column.
      didDrawCell: ({ section, column, row, cell }) => {
        if (section !== "body" || column.index !== 0) return;

        const original = submissions[row.index]?.summary?.original;
        const text = `${count(original)} rows`;
        drawText(
          layout,
          TYPE.descriptionMuted,
          text,
          cell.x + padding,
          cell.y + padding + nameHeight + u(4)
        );
      },
    },
  });
}

// One `EntitySummary` per entity, labelling the 340B ID as the page's summary does.
function renderEntities(layout, entities) {
  entities.forEach((entity) => {
    renderEntityCard(layout, {
      lines: [
        { token: TYPE.cardTitle, text: "340B ID" },
        { token: TYPE.bodyLarge, text: entity.id_340b || "" },
      ],
      rowCount: entity.rowCount,
      stats: [
        { title: "Manufacturers", count: entity.manufacturers.length },
        { title: "Products", count: entity.products.length },
        { title: "NDCs", count: Object.keys(entity.ndcs).length },
      ],
      table: {
        head: [["Manufacturer", "Product", "NDC", "Count"]],
        body: Object.values(entity.ndcs).map((details) => [
          details.manufacturer || "",
          details.product_name || "",
          details.ndc || "",
          count(details.rows),
        ]),
        columnStyles: { 3: { halign: "right" } },
      },
    });
  });
}

// A titled entity block: its count, the notice explaining what the rows in it mean, then the
// entities themselves.
function renderEntitySection(layout, { title, entities, notice, warning = false }) {
  if (!entities.length) return;

  const { u } = layout;

  layout.y += u(16);
  ensureSpace(layout, textHeight(layout, TYPE.bodyLarge, 1) + u(16) + tableHeight(layout, 1));
  layout.y += drawText(
    layout,
    TYPE.bodyLarge,
    `${title} (${entities.length})`,
    layout.margin,
    layout.y
  );
  layout.y += u(16);

  // ENTITY FILTERING DISABLED
  // renderNotice(layout, notice, { warning });
  renderEntities(layout, entities);
}

export default async function generateSummaryPdf(submissions = [], currentUser, title) {
  if (!submissions.length) return;

  const summary = buildSummaryData(submissions);
  // The page names an untitled submission for the moment it is looked at; so does this.
  const name = title || `Submission_${moment().format("X")}`;
  const layout = await createDocument();

  renderHeader(layout, {
    eyebrow: "Submission Preview",
    title: name,
    subtitle: `Date: ${moment().format("MMMM D, YYYY")}`,
    // The page's user comes from Rails as `full_name`; the receipt's payload names it `name`.
    user: { ...currentUser, name: currentUser?.full_name ?? currentUser?.name },
  });

  drawDivider(layout);
  renderStatCards(layout, [
    {
      title: "Original Rows",
      description: "Total rows uploaded across all files",
      value: summary.original,
    },
    {
      title: "Selected Rows",
      // ENTITY FILTERING DISABLED was "Rows with valid NDCs and 340BID permissions"
      description: "Rows with valid NDCs",
      value: summary.rows,
    },
    {
      title: "Rows for Submission",
      description: "Selected rows with no mapping errors",
      value: summary.valid,
    },
  ]);

  // `submission_preview__rows` stacks the two rows of cards 16px apart.
  layout.y += layout.u(16);
  renderStatCards(layout, [
    {
      title: "Entities",
      description: "Number of entities found in submission",
      value: summary.entities.length,
    },
    {
      title: "Manufacturers",
      description: "Number of manufacturers found in submission",
      value: summary.manufacturers,
    },
    {
      title: "NDCs",
      description: "Number of unique NDCs found in submission",
      value: uniq(summary.products).length,
    },
  ]);

  drawDivider(layout, "Files", tableHeight(layout, Math.min(submissions.length, 3)));
  renderFilesCard(layout, submissions);

  // ENTITY FILTERING DISABLED excludedEntities/excludedRows are destructured only for the
  // commented-out section below.
  const { entities, excludedEntities, excludedRows, rows } = summary;

  // ENTITY FILTERING DISABLED the excludedEntities half of this condition was:
  // if (entities.length || excludedEntities.length) {
  if (entities.length) {
    drawDivider(layout, "Covered Entities", layout.u(CARD_PADDING_PX * 4) + tableHeight(layout, 1));
  }

  /* ENTITY FILTERING DISABLED 
  renderEntitySection(layout, {
    title: "Entities Not Selected",
    entities: excludedEntities,
    warning: true,
    notice: `${count(excludedRows)} ${
      excludedRows === 1 ? "row was" : "rows were"
    } excluded because they belong to entities you do not have access to. These rows will not be submitted.`,
  });
  */

  renderEntitySection(layout, {
    // ENTITY FILTERING DISABLED was "Selected Entities"
    title: "Entities",
    entities,
    // ENTITY FILTERING DISABLED
    // notice: `${count(rows)} ${
    //   rows === 1 ? "row has" : "rows have"
    // } been selected because they belong to your entities.`,
  });

  renderFooter(layout, name);

  layout.doc.save(`${name}_summary.pdf`);
}
